import { EventEngineStateClientChangeFeed } from '@hds-products/event-runtime-entities-client';
import { useEventEngineStateStore } from '../pinia/eventEngineStateStore';
import { useAdminSdkStatusStore } from '../pinia/adminSdkStatusStore';
import { compare } from 'fast-json-patch';

class EventEngineStatePiniaVueAdapter {
    #_changeFeed;
    #_deltaServerClient;
    #_eventEngineStatePiniaStore;
    #_sdkStatusPiniaStore;
    #_entityIdToWatcherIdMap = new Map();
    constructor(deltaServerClient, eventEngineStateBaseUri, customHeaderFn) {
        this.#_deltaServerClient = deltaServerClient;
        this.#_changeFeed = new EventEngineStateClientChangeFeed(deltaServerClient, eventEngineStateBaseUri, customHeaderFn, null);
        this.#_eventEngineStatePiniaStore = useEventEngineStateStore();
        this.#_sdkStatusPiniaStore = useAdminSdkStatusStore();
        this.#_sdkStatusPiniaStore.updateChangeFeedInfo('event-engine-state', { title: 'Event Engine', description: 'Engine processing state for events', status: 'INITIALIZING' })
        this.onAdded = this.onAdded.bind(this);
        this.onUpdated = this.onUpdated.bind(this);
        this.onRemoved = this.onRemoved.bind(this);
    }
    async _connectAndStartChangeFeed(isRestart = false) {
        let attempts = 1;
        if (this.#_deltaServerClient.isConnected()) {
            await this.#_changeFeed.whenStarted();
            this.#_sdkStatusPiniaStore.updateChangeFeedInfo('event-engine-state', { status: 'CONNECTED', attempts })
        } else {
            while (!this.#_deltaServerClient.isConnected()) {
                try {
                    await this.#_deltaServerClient.whenConnected();
                    if (attempts === 1) {
                        console.info(`event-engine-state changefeed connected`);
                    } else {
                        console.info(`event-engine-state changefeed connected after attempt #${attempts}.`);
                    }
                    if (isRestart) {
                        await this.#_changeFeed.restart();
                    } else {
                        await this.#_changeFeed.whenStarted();
                    }
                    attempts = 1;
                    this.#_sdkStatusPiniaStore.updateChangeFeedInfo('event-engine-state', { status: 'CONNECTED', attempts })
                } catch (err) {
                    //A refused principal stays refused, so another attempt cannot succeed
                    if (err?.closeCode === 4403) {
                        this.#_sdkStatusPiniaStore.updateChangeFeedInfo('event-engine-state', { status: 'DISCONNECTED', attempts })
                        return;
                    }
                    attempts++;
                    console.error(`Attempt #${attempts}: event-engine-state changefeed error: ${err}`);
                    this.#_sdkStatusPiniaStore.updateChangeFeedInfo('event-engine-state', { status: 'DISCONNECTED', attempts })
                    await new Promise(resolve => setTimeout(resolve, 1000));
                }
            }
        }
    }
    async initialize() {
        this.#_changeFeed.addFailureListener(async (error) => {
            console.error(`Change Feed Failure (EventEngineStateChangeFeed) => ${error.message}`);
            this.#_sdkStatusPiniaStore.updateChangeFeedInfo('event-engine-state', { status: 'DISCONNECTED' })
            await this._connectAndStartChangeFeed(true);
        }, this);
        await this._connectAndStartChangeFeed();
    }
    async watchEventEngineState(eventEngineStateId) {
        // Check if the entity watcher map does not already have a watcher for the provided eventEngineStateId
        if (!this.#_entityIdToWatcherIdMap.has(eventEngineStateId)) {
            const watcher = await this.#_changeFeed.watch(eventEngineStateId);
            watcher.on('entity-added', this.onAdded);
            watcher.on('entity-updated', this.onUpdated);
            watcher.on('entity-removed', this.onRemoved);
            this.#_entityIdToWatcherIdMap.set(eventEngineStateId, watcher.watchId);
            const eventEngineStateEntity = watcher.getEntity(eventEngineStateId);
            if (eventEngineStateEntity) { await this.onAdded(eventEngineStateEntity); }
        }
    }
    async unwatchEventEngineState(eventEngineStateId) {
        // Check if the entity watcher map has a key for the provided eventEngineStateId
        if (this.#_entityIdToWatcherIdMap.has(eventEngineStateId)) {
            const watcherId = this.#_entityIdToWatcherIdMap.get(eventEngineStateId);
            const watcher = this.#_changeFeed.getWatcher(watcherId);
            watcher.off('entity-added', this.onAdded);
            watcher.off('entity-updated', this.onUpdated);
            watcher.off('entity-removed', this.onRemoved);
            this.#_changeFeed.unwatch(watcherId);
            this.#_entityIdToWatcherIdMap.delete(eventEngineStateId);
            this.onRemoved(eventEngineStateId);
        }
    }
    onAdded(eventEngineStateEntity) {
        this.#_eventEngineStatePiniaStore.addEventEngineState(eventEngineStateEntity.toJSON());
    }
    onUpdated(eventEngineStateId, prevModel, newEntity) {
        const jsonPatch = compare(prevModel, newEntity.toJSON());
        if (jsonPatch.length > 0) {
            this.#_eventEngineStatePiniaStore.applyJsonPatch(eventEngineStateId, jsonPatch);
        }
    }
    onRemoved(eventEngineStateId) {
        this.#_eventEngineStatePiniaStore.removeEventEngineState(eventEngineStateId);
    }
}

export { EventEngineStatePiniaVueAdapter };
export default EventEngineStatePiniaVueAdapter;
